import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Copy, ExternalLink, Link2, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHead } from "@/components/bits";
import { publicApi } from "@/services/api-client";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { useI18n } from "@/i18n/i18n-context";
import { countryOf } from "@/types/dashboard";
import {
  Empty,
  ErrorBox,
  KV,
  Pager,
  SkeletonRows,
  Tag,
  buildQuery,
  fmtAmount,
  fmtWhen,
  normalizeAmount,
  pageInfo,
  pick,
  record,
  rows,
  safeUrl,
  text,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";

const PER_PAGE = 25;

export default function PaymentLinksPage() {
  return <Links />;
}

function Links() {
  const { lang } = useI18n();
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [create, setCreate] = useState(false);
  useEffect(() => setPage(1), [status]);
  const list = useServer<unknown>(
    buildQuery("/dashboard/payment-links", {
      status,
      per_page: PER_PAGE,
      page,
    }),
  );
  const items = useMemo(() => rows(list.data), [list.data]);
  const info = pageInfo(list.data, list.meta, items.length, PER_PAGE);

  return (
    <>
      <PageHead
        title="Liens de paiement"
        sub="Encaissez sans intégration : chaque lien vit dans un pays et sa devise."
      >
        <Button
          onClick={() => setCreate(true)}
          className="gap-1.5"
          data-testid="button-new-link"
        >
          <Plus className="h-4 w-4" /> Nouveau lien
        </Button>
      </PageHead>
      <div className="mb-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-44 bg-card" aria-label="Statut">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {["ACTIVE", "INACTIVE", "EXPIRED"].map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rise overflow-hidden rounded-xl border bg-card">
        {list.error ? (
          <ErrorBox className="m-4" error={list.error} onRetry={list.reload} />
        ) : list.loading && !items.length ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <Empty
            icon={<Link2 className="h-5 w-5" />}
            title="Aucun lien de paiement"
          >
            Créez un lien pour une facture ou une commande.
          </Empty>
        ) : (
          <ul className="divide-y">
            {items.map((l) => (
              <li
                key={text(l, "id")}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid={`row-link-${text(l, "id")}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {text(l, "description") || text(l, "reference")}
                  </p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {text(l, "reference")} ·{" "}
                    {l.expires_at
                      ? `expire ${fmtWhen(l.expires_at)}`
                      : "Lien permanent"}
                    {text(l, "fee_payer") && (
                      <span className="ml-2 rounded bg-accent px-1.5 py-0.5 font-sans text-[10px] font-semibold">
                        {text(l, "fee_payer").toUpperCase() === "CUSTOMER"
                          ? "Frais : Client"
                          : "Frais : Marchand"}
                      </span>
                    )}
                  </p>
                </div>
                <span className="tnum text-sm font-bold">
                  {Boolean(l.allow_custom_amount) ||
                  !l.amount ||
                  Number(l.amount) <= 0
                    ? lang === "en"
                      ? "Custom amount"
                      : "Montant libre"
                    : fmtAmount(l.amount, pick(l, "currency", "currency_code"))}
                </span>
                <Tag value={text(l, "status")} />
                <LinkActions link={l} />
              </li>
            ))}
          </ul>
        )}
        {(items.length > 0 || page > 1) && (
          <Pager info={info} loading={list.loading} onPage={setPage} />
        )}
      </div>
      <CreateDialog
        open={create}
        onClose={() => setCreate(false)}
        onCreated={() => {
          list.reload();
        }}
      />
    </>
  );
}

const CHECKOUT_ORIGIN = "https://pay.bizconnectpay.com";

function checkoutUrl(link: ApiRecord): string | null {
  const code = text(link, "public_code", "code");
  if (code) return `${CHECKOUT_ORIGIN}/link/${encodeURIComponent(code)}`;

  const supplied = pick(link, "checkout_url", "public_url", "url", "link");
  if (typeof supplied !== "string" || !supplied) return null;
  try {
    const parsed = new URL(supplied, CHECKOUT_ORIGIN);
    if (!parsed.pathname.startsWith("/link/")) return null;
    return new URL(parsed.pathname, CHECKOUT_ORIGIN).href;
  } catch {
    return null;
  }
}

function LinkActions({ link }: { link: ApiRecord }) {
  const [copied, setCopied] = useState(false);
  const url = checkoutUrl(link);

  if (!url)
    return (
      <span className="text-xs text-muted-foreground">Lien indisponible</span>
    );

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => {
          const write = navigator.clipboard?.writeText(url);
          if (!write) return;
          void write.then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        <Copy className="h-3.5 w-3.5" /> {copied ? "Copié" : "Copier"}
      </Button>
      <Button size="sm" variant="outline" asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="gap-1.5"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Ouvrir
        </a>
      </Button>
    </div>
  );
}

function PublicUrl({ url }: { url: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!url)
    return (
      <p className="text-xs text-muted-foreground">
        Aucune URL publique sûre retournée par le serveur.
      </p>
    );
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-accent/50 p-2">
      <code className="min-w-0 flex-1 truncate text-xs">{url}</code>
      <Button
        size="sm"
        variant="outline"
        className="h-8 bg-card"
        aria-label="Copier le lien"
        onClick={() =>
          void navigator.clipboard?.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
        }
      >
        <Copy className="h-4 w-4" />
      </Button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Ouvrir"
        className="inline-flex h-8 items-center rounded-md border bg-card px-2"
      >
        <ExternalLink className="h-4 w-4" />
      </a>
      {copied && (
        <span className="text-[11px] font-semibold text-emerald-700">
          Copié
        </span>
      )}
    </div>
  );
}

function CreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { wallets } = useDashboard();
  const { t, lang } = useI18n();
  const feesRes = useServer<unknown>(open ? "/dashboard/fees" : null);
  const collectionFeePct = useMemo(() => {
    const f = record(feesRes.data);
    return Number(
      text(f, "collection_fee_percentage", "collection.percentage") || "4.5",
    );
  }, [feesRes.data]);
  const pairs = useMemo(() => {
    const m = new Map<string, { country: string; currency: string }>();
    wallets
      .filter((w) => w.status === "ACTIVE" && w.country !== "INT")
      .forEach((w) =>
        m.set(`${w.country}:${w.currency}`, {
          country: w.country,
          currency: w.currency,
        }),
      );
    return Array.from(m.entries());
  }, [wallets]);
  const [pair, setPair] = useState("");
  const [pricingType, setPricingType] = useState<"FIXED" | "CUSTOM">("FIXED");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [expires, setExpires] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [feePayer, setFeePayer] = useState<"MERCHANT" | "CUSTOMER">("MERCHANT");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [created, setCreated] = useState<ApiRecord | null>(null);
  useEffect(() => {
    if (open) {
      setPair(pairs[0]?.[0] ?? "");
      setPricingType("FIXED");
      setAmount("");
      setDescription("");
      setExpires("");
      setMaxUses("");
      setFeePayer("MERCHANT");
      setErr(null);
      setCreated(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const value = normalizeAmount(amount);
  const uses = maxUses.trim() ? Number(maxUses) : null;
  const sel = pairs.find(([k]) => k === pair)?.[1];
  const valid = Boolean(
    sel &&
    description.trim() &&
    (pricingType === "CUSTOM" || (value && Number(value) > 0)) &&
    (!expires || new Date(expires).getTime() > Date.now()) &&
    (uses === null || (Number.isInteger(uses) && uses > 0)),
  );

  const feePreview = useMemo(() => {
    const base = Number(value) || 0;
    if (base <= 0 || !sel) return null;
    const isZero = ["XAF", "XOF", "GNF", "RWF", "UGX"].includes(sel.currency);
    const round = (v: number) =>
      isZero ? Math.round(v) : Math.round(v * 100) / 100;
    const fee = round((base * collectionFeePct) / 100);
    if (feePayer === "CUSTOMER") {
      return {
        charged: round(base + fee),
        fee,
        net: round(base),
        currency: sel.currency,
      };
    }
    return {
      charged: round(base),
      fee,
      net: Math.max(0, round(base - fee)),
      currency: sel.currency,
    };
  }, [value, sel, collectionFeePct, feePayer]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || !sel) return;
    setBusy(true);
    setErr(null);
    try {
      const payload: Record<string, unknown> = {
        currency: sel.currency,
        description: description.trim(),
        fee_payer: feePayer,
        allow_custom_amount: pricingType === "CUSTOM",
        amount_type: pricingType,
      };
      if (pricingType === "FIXED" || value) {
        payload.amount = value;
      }
      if (expires) {
        payload.expires_at = new Date(expires).toISOString();
      }
      if (uses && uses > 0) {
        payload.max_uses = uses;
      }

      const { data } = await publicApi<ApiRecord>("/dashboard/payment-links", {
        method: "POST",
        body: payload,
      });
      const l = record(record(data).payment_link ?? data);
      setCreated({
        ...l,
        public_url: checkoutUrl(l),
      });
      onCreated("");
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[520px]">
        {created ? (
          <div className="grid gap-3" data-testid="link-created">
            <DialogTitle>Lien créé</DialogTitle>
            <PublicUrl url={safeUrl(pick(created, "public_url", "url"))} />
            <KV data={created} exclude={["public_url", "url"]} />
            <Button onClick={onClose}>Fermer</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Nouveau lien de paiement</DialogTitle>
              <DialogDescription>
                Pays et devise proviennent de vos wallets actifs.
              </DialogDescription>
            </DialogHeader>
            {pairs.length === 0 && (
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                Aucun wallet local actif pour encaisser.
              </p>
            )}
            {/* Devise et Montant */}
            <div className="space-y-3 rounded-xl border bg-slate-50/70 p-3.5">
              <p className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Devise et Montant
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setPricingType("FIXED")}
                  className={`flex flex-col text-left rounded-lg border p-3 transition-all ${
                    pricingType === "FIXED"
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <span className="text-xs font-bold text-slate-900">
                    Montant fixe
                  </span>
                  <span className="mt-1 text-[11px] text-slate-500 leading-tight">
                    Le client paye le montant déterminé par vos soins.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setPricingType("CUSTOM")}
                  className={`flex flex-col text-left rounded-lg border p-3 transition-all ${
                    pricingType === "CUSTOM"
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <span className="text-xs font-bold text-slate-900">
                    Montant libre (Donation / Pourboire)
                  </span>
                  <span className="mt-1 text-[11px] text-slate-500 leading-tight">
                    Le client saisit lui-même la somme souhaitée.
                  </span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="grid gap-1.5">
                  <Label htmlFor="pl-pair">Pays / devise</Label>
                  <Select value={pair} onValueChange={setPair}>
                    <SelectTrigger id="pl-pair">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      {pairs.map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          {countryOf(v.country).name} · {v.currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {pricingType === "FIXED" ? (
                  <div className="grid gap-1.5">
                    <Label htmlFor="pl-amount">Montant fixe</Label>
                    <Input
                      id="pl-amount"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Ex: 5000"
                      data-testid="input-link-amount"
                    />
                  </div>
                ) : (
                  <div className="grid gap-1.5">
                    <Label htmlFor="pl-amount-custom">
                      Montant suggéré (optionnel)
                    </Label>
                    <Input
                      id="pl-amount-custom"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Optionnel"
                      data-testid="input-link-amount"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="pl-desc">Description</Label>
              <Input
                id="pl-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Facture 1001"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="pl-exp">Expiration (optionnel)</Label>
                <Input
                  id="pl-exp"
                  type="datetime-local"
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                />
                <span className="text-[10px] text-muted-foreground">
                  Laissez vide pour un lien permanent
                </span>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="pl-uses">
                  {lang === "en"
                    ? "Max uses (optional)"
                    : "Utilisations max (optionnel)"}
                </Label>
                <Input
                  id="pl-uses"
                  type="number"
                  min={1}
                  step={1}
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="Illimité"
                />
                <span className="text-[10px] text-muted-foreground">
                  Laissez vide pour utilisations illimitées
                </span>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pl-fee-payer">
                {t("settings.feePayerLabel")}
              </Label>
              <Select
                value={feePayer}
                onValueChange={(v) => setFeePayer(v as "MERCHANT" | "CUSTOMER")}
              >
                <SelectTrigger
                  id="pl-fee-payer"
                  data-testid="select-link-fee-payer"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MERCHANT">
                    {t("settings.feePayerMerchantOption")}
                  </SelectItem>
                  <SelectItem value="CUSTOMER">
                    {t("settings.feePayerCustomerOption")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {feePreview && (
              <div
                className="rounded-lg border bg-accent/40 p-3 text-xs space-y-1.5"
                data-testid="link-fee-preview"
              >
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("settings.simCustomerPays")} :
                  </span>
                  <span className="font-mono font-bold">
                    {fmtAmount(feePreview.charged, feePreview.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("settings.simFeeAmount")} ({collectionFeePct}%) :
                  </span>
                  <span className="font-mono font-semibold text-amber-700">
                    {fmtAmount(feePreview.fee, feePreview.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t pt-1.5">
                  <span className="font-semibold">
                    {t("settings.simMerchantNet")} :
                  </span>
                  <span className="font-mono font-bold text-emerald-700">
                    {fmtAmount(feePreview.net, feePreview.currency)}
                  </span>
                </div>
              </div>
            )}
            <ErrorBox error={err} />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={busy}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={!valid || busy}
                data-testid="button-submit-link"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer
                le lien
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
